import { use } from "react";
import { buildApiUrl } from "../../services/apiConfig";
import { v4 as uuidv4 } from 'uuid';

class WealthChatbotService {

    getOrGenerateThreadID() {
        let threadID = sessionStorage.getItem("wealth_thread_id");
        if (!threadID) {
            threadID = uuidv4();
            sessionStorage.setItem("wealth_thread_id", threadID);
        }
        return threadID;
    }

    getPageName(pathname) {
        const path = pathname.substring(1);
        if (!path) return 'WealthManagement';
        return path.split('-')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join('')
            || 'WealthManagement';
    }

    async sendMessageV2(input, cust_id, userId, useCaseId, llmCfg = {}, templateId = null) {
        const formData = new FormData();
        formData.append("query", input);
        formData.append("thread_id", this.getOrGenerateThreadID());
        formData.append("user_id", userId);
        formData.append("cust_id", cust_id);
        formData.append("use_case_id", useCaseId);
        formData.append("agent_id", "63493");
        formData.append("domain_id", "");
        formData.append("safety_config", JSON.stringify({
            enable_input_guardrails: true,
            enable_output_guardrails: true,
            llm_cfg: llmCfg
        }));
        if (templateId !== null) formData.append("template_id", templateId);

        const response = await fetch(buildApiUrl( 'NOVA_V2', '/Nova_chat_stream'), {
            method: "POST",
            body: formData,
        });

        if (!response.ok) {
            throw new Error(`Server responded with ${response.status}`);
        }

        return response;
    }

    async sendMessage(input, files, cust_id, userId, pageName, useCaseId, runId = null) {
        const formData = new FormData();
        formData.append("query", input);
        formData.append("cust_id", cust_id);
        formData.append("advisor_id", userId);
        formData.append("page_name", pageName);
        formData.append("use_case_id", useCaseId);
        formData.append("agent_id", "63493");
        if (runId) formData.append("run_id", runId);

        const threadId = this.getOrGenerateThreadID();
        formData.append("thread_id", threadId);

        files.forEach(file => {
            formData.append("files", file);
        });

        const response = await fetch(buildApiUrl('NOVA', '/Nova_chat_stream'), {
            method: "POST",
            body: formData,
        });

        if (!response.ok) {
            throw new Error(`Server responded with ${response.status}`);
        }

        return response;
    }

    async uploadFiles(files, userId) {
        const formData = new FormData();
        files.forEach(file => {
            formData.append('files', file);
        });
        formData.append('collection_name', `${userId}`);


        const response = await fetch(buildApiUrl('UPLOAD', '/upload-documents'), {
            method: 'POST',
            body: formData,
        });

        if (!response.ok) {
            throw new Error(`Upload failed with status: ${response.status}`);
        }

        return response;
    }

    async createWidget(advisorId, custId, useCaseId, tabId) {
        const formData = new FormData();
        formData.append("advisor_id", advisorId);
        formData.append("cust_id", custId);
        formData.append("use_case_id", useCaseId);
        formData.append("tab_id", tabId);

        const response = await fetch(buildApiUrl('NOVA', '/create_widget'), {
            method: "POST",
            body: formData,
        });

        if (!response.ok) {
            throw new Error(`Widget creation failed with status: ${response.status}`);
        }

        return response.json();
    }

    async getSuggestions() {
        try {
            const response = await fetch(buildApiUrl('NOVA_V2', '/flow_templates'));
            if (!response.ok) throw new Error(`Suggestions API responded with ${response.status}`);
            const data = await response.json();
            return (data.templates || []).filter(t => t.enabled);
        } catch (error) {
            console.error('Error fetching suggestions:', error);
            return [];
        }
    }

    buildApiUrl(service, endpoint) {
        return buildApiUrl(service, endpoint);
    }

    async getSummarizedAudit(runId) {
        try {
            const sumAuditUrl = buildApiUrl('SUM_AUDIT', `/${runId}/summary`);

            const response = await fetch(sumAuditUrl, {
                method: 'GET',
            });

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error(`API Error (summarize_audit):`, error);
            throw error;
        }
    };

    downsampleBuffer(buffer, originalSampleRate, targetSampleRate) {
        if (originalSampleRate === targetSampleRate) {
            return buffer;
        }

        const ratio = originalSampleRate / targetSampleRate;
        const newLength = Math.round(buffer.length / ratio);
        const result = new Float32Array(newLength);

        let offsetResult = 0;
        let offsetBuffer = 0;

        while (offsetResult < result.length) {
            const nextOffsetBuffer = Math.round((offsetResult + 1) * ratio);
            let accum = 0;
            let count = 0;

            for (let i = offsetBuffer; i < nextOffsetBuffer && i < buffer.length; i++) {
                accum += buffer[i];
                count++;
            }

            result[offsetResult] = accum / count;
            offsetResult++;
            offsetBuffer = nextOffsetBuffer;
        }

        return result;
    }

    floatTo16BitPCM(input) {
        const output = new Int16Array(input.length);
        for (let i = 0; i < input.length; i++) {
            const s = Math.max(-1, Math.min(1, input[i]));
            output[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
        }
        return output.buffer;
    }

    getTranscriptionSocketUrl() {
        return buildApiUrl('SOCKET');
    }

    /**
  * Generates text-to-speech audio
  * @param {string} text - The text to convert to speech
  * @returns {Promise<Object>} The generated audio blob
  */
    async generateTextToSpeech(text) {
        try {
            const response = await fetch(buildApiUrl('TTS', '/tts_normalize'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ text, summ_flag: true })
            });

            if (!response.ok) {
                throw new Error(`TTS API responded with status: ${response.status}`);
            }

            return { success: true, audioBlob: await response.blob() };
        } catch (error) {
            console.error('Error generating text-to-speech:', error);
            return { success: false, error: error.message };
        }
    }

    /**
     * Processes audio for transcription
     * @param {Blob} audioBlob - The audio blob to process
     * @returns {Promise<Object>} The processed audio data
     */
    async processAudio(audioBlob) {
        try {
            const formData = new FormData();
            formData.append('file', audioBlob, 'audio.wav');

            const response = await fetch(buildApiUrl('AUDIO', '/process_audio'), {
                method: 'POST',
                body: formData
            });

            if (!response.ok) {
                throw new Error(`Audio processing API responded with status: ${response.status}`);
            }

            return { success: true, data: await response.json() };
        } catch (error) {
            console.error('Error processing audio:', error);
            return { success: false, error: error.message };
        }
    }
}

export default new WealthChatbotService();